import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './Sidebar.module.scss';
import Icon from '@/components/atomics/Icons';
import { IconName } from '@/components/atomics/Icons';
import { clearLocalStorages, getLocalStorage } from '@/utils/storages';
import { useNavigate } from 'react-router-dom';
import { extractRoleString } from '@/utils/admin';

type SidebarItem = {
  icon?: IconName;
  label: string;
  to?: string;
  subMenu?: SidebarItem[];
};

type SidebarType = {
  mini?: boolean;
}

const className = 'sidebar';

const sidebarItems: SidebarItem[] = [
  { icon: 'Dashboard', label: 'Dashboard', to: '/dashboard' },
  { icon: 'StarBubble', label: 'NPS & Rating Apps', to: '/list-feedback' },
];

const Sidebar: React.FC<SidebarType> = ({ mini }) => {
  const navigate = useNavigate();      
  const location = useLocation();
  const [expandedSubmenu, setExpandedSubmenu] = React.useState<string[]>([]);
  const [profileExpanded, setProfileExpanded] = React.useState<boolean>(false);
  const [menu, setMenu] = React.useState<SidebarItem[]>(sidebarItems);
  let userName = 'User Name';
  let userRole = 'Admin';
  let userLevel = 100;

  useEffect(() => { 
    const userData = getLocalStorage('user_data');
    if (userData) {
      try {
        const parsedUserData = JSON.parse(userData);
        if (parsedUserData?.level === 3) {
          setMenu([
            { icon: 'StarBubble', label: 'NPS & Rating Apps', to: '/list-feedback' },
          ]);
        }
      } catch {}
    }
  }, []);

  try {
    const userData = JSON.parse(getLocalStorage('user_data') || '{}');
    userName = userData?.username || userName;
    userRole = extractRoleString(userData?.level) || userRole;
    userLevel = userData?.level;
  } catch {}

  return (
    <aside className={[styles[className], mini ? styles[className + '__mini'] : ''].join(' ')}
      data-testid="sidebar">
      <div className={styles[className + '__logo']}>
        {
          mini ? (
            <img alt="Logo" src="/images/logo-mini.svg" width="30" />
          ) : (            
            <img alt="Logo" src="/images/logo.png" width="96" />
          )
        }
      </div>
      <nav>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {menu.map((item, idx) => (
            <li key={item.to ?? item.label + idx}>
              {item.to ? (
                <Link
                  className={styles[className + '__link' + (location.pathname === item.to ? '_active' : '')]}
                  to={item.to}
                  style={{
                    textDecoration: 'none',
                  }}
                >
                  {item?.icon && <Icon className={styles[className + '__link_icon']} name={item?.icon} />}
                  {!mini && item.label}
                </Link>
              ) : (
                <ul>
                  <li>
                    <span className={styles[className + '__link' + (item.subMenu?.some(sub => sub.to && location.pathname === sub.to) ? '_active' : '')]}
                      onClick={() => {
                        if (expandedSubmenu.includes(idx.toString())) {
                          setExpandedSubmenu(expandedSubmenu.filter(sub => sub !== idx.toString()));
                        } else {
                          setExpandedSubmenu([...expandedSubmenu, idx.toString()]);
                        }
                      }}>
                      {item?.icon && <Icon className={styles[className + '__link_icon']} name={item?.icon} />}
                      {item.label}
                      <Icon className={styles[className + '__link_chevron']} name="Chevron"
                        style={{ transform: expandedSubmenu.includes(idx.toString())
                          ? 'rotate(0deg)' : 'rotate(180deg)'}} />
                    </span>
                  </li>
                  {expandedSubmenu.includes(idx.toString()) && item.subMenu?.map((subItem, subIdx) => (
                    <li key={subItem.to ?? subItem.label + subIdx}>
                      <Link
                        className={styles[className + '__link' + (location.pathname === subItem.to ? '_active' : '')]}
                        to={subItem.to ?? '#'}
                        style={{
                          textDecoration: 'none',
                        }}
                      >
                        {subItem.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </nav>
      <div className={styles[className + '__profile']}>
        <ul>
          <li>
            <span className={styles[className + '__profile_info']}>
              <img
                alt="Profile"
                src="/images/profile.png"
                width="40"
                height="40"
                style={{ borderRadius: '50%' }}
              />
              {!mini && <span className={styles[className + '__profile_info_name']}>
                <b>{userName}</b>
                {userRole}
              </span>}
            </span>
          </li>
          <li>
            <span className={styles[className + '__link']} onClick={() => {
                setProfileExpanded(!profileExpanded);
              }}>
              <Icon className={styles[className + '__link_icon']} name="Gear" />
              {!mini && 'Pengaturan'}
              {!mini && <Icon className={styles[className + '__link_chevron']} name="Chevron"
                style={{ transform: profileExpanded ? 'rotate(0deg)' : 'rotate(180deg)'}} />}
            </span>
          </li>
          {(profileExpanded) && (
            <>
              <li>
                <Link
                  className={styles[className + '__link' + (location.pathname === '/profile' ? '_active' : '')]}
                  to="/setting"
                  style={{
                    textDecoration: 'none',
                  }}
                >
                  <Icon className={styles[className + '__link_icon']} name="PeopleBold" />
                  {!mini && 'Profil'}
                </Link>
              </li>
              {userLevel === 0 && <li>
                <Link
                  className={styles[className + '__link' + (location.pathname === '/password' ? '_active' : '')]}
                  to="/list-admin"
                  style={{
                    textDecoration: 'none',
                  }}
                >
                  <Icon className={styles[className + '__link_icon']} name="Peoples" />
                  {!mini && 'List Admin'}
                </Link>
              </li>}
            </>
          )}
          <li>
            <Link
              className={styles[className + '__link']}
              onClick={async () => {
                await clearLocalStorages();

                navigate('/');
              }}
              to="/"
              style={{
                textDecoration: 'none',
              }}
            >
              <Icon className={styles[className + '__link_icon']} name="Logout" />
              {!mini && 'Keluar'}
            </Link>
          </li>
        </ul>
      </div>
    </aside>
  );
};

export default Sidebar;